import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, BarChart,
} from 'recharts';
import LoadingSpinner from '../LoadingSpinner';
import CriticalMaterialsTab from './CriticalMaterialsTab';
import './Operations.css';
import './LowStockDashboard.css';
import './OrderingDashboard.css';

// ORDERING — the one purchasing page: a tab per material type.
//   Oils | Wax        → the live-SuiteQL WEIGHT engine (full oil featureset:
//                       shortages, channel split, Overstock/JIT, PO builder,
//                       weekly projection charts). Everything in pounds.
//   Vessels | Wicks | Labels | Packaging | Other
//                     → the mirror EACH engine + seasonal/trend enrichment
//                       (editable buy quantities, per-vendor PO builder,
//                       expected-delivery PO panel). Plain units, except the
//                       rare weight-based rows which arrive as pounds.
// Customer production watch: Oils and Vessels tabs each render their own
// (oils from the weight engine payload, vessels from the mirror payload's
// SuiteQL open-WO pass), above the shortage table.
// /oil-ordering and /materials-ordering redirect here with the matching tab.

const TABS = [
  { key: 'critical', label: 'Critical', kind: 'critical' },
  { key: 'oils', label: 'Oils', kind: 'weight' },
  { key: 'wax', label: 'Wax', kind: 'weight' },
  { key: 'vessels', label: 'Vessels', kind: 'each' },
  { key: 'wicks', label: 'Wicks', kind: 'each' },
  { key: 'labels', label: 'Labels', kind: 'each' },
  { key: 'packaging', label: 'Packaging', kind: 'each' },
  { key: 'other', label: 'Other', kind: 'each' },
];
const TAB_KIND = Object.fromEntries(TABS.map((t) => [t.key, t.kind]));

// Chart series colors — validated categorical palette (CVD-safe, ≥3:1 on white).
const C = {
  wo: '#B4592F',       // work-order committed demand
  so: '#2E6E9E',       // sales-order residual demand
  poIn: '#6B8E3E',     // inbound PO receipts
  level: '#3D3A33',    // projected stock level line
  seasonal: '#8A8578', // last-year reference (neutral, dashed)
  grid: '#EDEAE4',
};

const FLAG_META = {
  ORDER_NOW: { label: 'Order now', cls: 'oil-pill-serious' },
  VERIFY_PO: { label: 'Verify PO', cls: 'oil-pill-warning' },
  OUT_AT_PRODUCTION: { label: 'Out at production', cls: 'oil-pill-serious' },
  OUT: { label: 'Out of stock', cls: 'oil-pill-serious' },
  FUTURE_COMMIT: { label: 'Future commit', cls: 'oil-pill-info' },
  STALE_PO: { label: 'Stale PO', cls: 'oil-pill-muted' },
  NEGATIVE_STOCK: { label: 'Negative stock', cls: 'oil-pill-muted' },
  DEAD_STOCK: { label: 'Dead stock', cls: 'oil-pill-serious' },
  OVERSTOCK: { label: 'Overstock', cls: 'oil-pill-warning' },
  DEMAND_MAY_BE_DISCONTINUED: { label: 'Demand may be discontinued', cls: 'oil-pill-warning' },
  NEW_COMPONENT: { label: 'New — no history', cls: 'oil-pill-info' },
  SEASONAL_DEMAND: { label: 'Seasonal (LY)', cls: 'oil-pill-info' },
  BOM_SUBSTITUTED: { label: 'Legacy — update BOMs', cls: 'oil-pill-muted' },
  WATCH: { label: 'Watch', cls: 'oil-pill-watch' },
  OK: { label: 'OK', cls: 'oil-pill-ok' },
};
const ACTION_FLAGS = ['ORDER_NOW', 'VERIFY_PO', 'OUT_AT_PRODUCTION', 'FUTURE_COMMIT', 'WATCH'];

const ORDER_STATUS_META = {
  LATE: { label: 'Late', cls: 'oil-pill-serious' },
  AT_RISK: { label: 'At risk', cls: 'oil-pill-serious' },
  NO_MAPPING: { label: 'No oil mapping', cls: 'oil-pill-warning' },
  ORDER_SOON: { label: 'Order soon', cls: 'oil-pill-warning' },
  COVERED: { label: 'Covered', cls: 'oil-pill-ok' },
};

const CHANNEL_LABEL = { intercompany: 'CF stores', large: 'Large orders', independent: 'Independents' };

const fmtLb = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
const fmtQty = fmtLb;
const fmtMoney = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
const shortSku = (sku) => (sku || '').replace(/^OIL-/, '').replace(/^Wax-/i, '');
const trimVendor = (name) => (name || '')
  .replace(' Fragrances Corporation', '').replace(' Creations', '')
  .replace(' International Fragrances', '').replace(' Fragrances Ltd.', '').replace(' Fragrances', '');
// 'YYYY-MM-DD' -> 'M/D/YY'
const fmtDate = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s)
  ? `${+s.slice(5, 7)}/${+s.slice(8, 10)}/${s.slice(2, 4)}` : (s || '—'));

function Th({ label, col, sortKey, sortDir, handleSort, align, title }) {
  const active = sortKey === col;
  return (
    <th
      className={`low-stock-th ${align === 'right' ? 'low-stock-th-right' : ''} ${active ? 'low-stock-th-active' : ''}`}
      onClick={() => handleSort(col)}
      title={title}
    >
      {label}
      {active && <span className="low-stock-sort-arrow">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>}
    </th>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// WEIGHT (oils / wax) — the full oil-engine view, pixel-parity with the old
// /oil-ordering page. Everything below down to the "EACH" banner is the
// original Oil Ordering UI, parameterized only by type key + label.
// ════════════════════════════════════════════════════════════════════════════

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="oil-chart-tip">
      <div className="oil-chart-tip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="oil-chart-tip-row">
          <span className="oil-chart-tip-swatch" style={{ background: p.color || p.stroke }} />
          <span>{p.name}</span>
          <span className="oil-chart-tip-val">{Math.round(p.value * 10) / 10} lb</span>
        </div>
      ))}
    </div>
  );
}

function ChannelBar({ channels }) {
  const total = channels.intercompany + channels.large + channels.independent;
  if (total <= 0) return <p className="oil-detail-empty">No open SO demand in horizon</p>;
  const keys = ['large', 'intercompany', 'independent'];
  const colors = { large: C.wo, intercompany: C.so, independent: C.poIn };
  return (
    <div>
      <div className="oil-channel-bar">
        {keys.map((k) => channels[k] > 0 && (
          <div key={k} className="oil-channel-seg" style={{ width: `${(channels[k] / total) * 100}%`, background: colors[k] }} />
        ))}
      </div>
      <div className="oil-channel-legend">
        {keys.map((k) => channels[k] > 0 && (
          <span key={k} className="oil-channel-item">
            <span className="oil-chart-tip-swatch" style={{ background: colors[k] }} />
            {CHANNEL_LABEL[k]} {fmtLb(channels[k])} lb
          </span>
        ))}
      </div>
    </div>
  );
}

function WeightDetail({ oil, horizonDays }) {
  const weekly = (oil.weekly || []).map((w) => ({ ...w, label: w.week.slice(5) }));
  const monthly = (oil.monthly_consumption || []).map((m) => ({ ...m, label: m.month.slice(2) }));
  const seasonalWeekly = (oil.seasonal_trend_lb ?? oil.seasonal_lb) / Math.max(1, horizonDays / 7);

  return (
    <div className="oil-detail">
      <div className="oil-detail-charts">
        <div className="oil-detail-chart">
          <h4>Next {horizonDays} days — demand &amp; projected stock</h4>
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={weekly} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6E6A60' }} tickLine={false} axisLine={{ stroke: C.grid }} />
              <YAxis tick={{ fontSize: 11, fill: '#6E6A60' }} tickLine={false} axisLine={false} width={44} unit=" lb" />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconSize={9} />
              <Bar dataKey="wo_lb" name="Work orders" stackId="d" fill={C.wo} maxBarSize={26} />
              <Bar dataKey="so_lb" name="SO residual" stackId="d" fill={C.so} radius={[3, 3, 0, 0]} maxBarSize={26} />
              <Line type="stepAfter" dataKey="level_lb" name="Stock (no POs)" stroke={C.level} strokeWidth={2} dot={false} />
              <Line type="stepAfter" dataKey="level_with_po_lb" name="If POs arrive" stroke={C.poIn} strokeWidth={2} strokeDasharray="5 4" dot={false} />
              <ReferenceLine y={seasonalWeekly} stroke={C.seasonal} strokeDasharray="5 4"
                label={{ value: 'last-yr pace (trend-adj)', position: 'insideTopRight', fontSize: 11, fill: C.seasonal }} />
              <ReferenceLine y={0} stroke={C.grid} />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="oil-detail-caption">Dashed line shows stock if open POs arrive on time.</p>
        </div>
        <div className="oil-detail-chart">
          <h4>Monthly consumption — 24 months</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barCategoryGap="18%">
              <CartesianGrid stroke={C.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6E6A60' }} tickLine={false} axisLine={{ stroke: C.grid }} interval={2} />
              <YAxis tick={{ fontSize: 11, fill: '#6E6A60' }} tickLine={false} axisLine={false} width={44} unit=" lb" />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="lb" name="Consumed" fill={C.wo} radius={[3, 3, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
          <p className="oil-detail-caption">From assembly builds, converted to pounds.</p>
        </div>
      </div>

      <div className="oil-detail-tables">
        <div className="oil-detail-table-block">
          <h4>Demand by channel (next {horizonDays}d)</h4>
          <ChannelBar channels={oil.so_channels} />
          {oil.future_commit_lb > 0 && (
            <p className="oil-detail-caption" style={{ marginTop: 8 }}>
              + <strong>{fmtLb(oil.future_commit_lb)} lb</strong> committed on large orders shipping beyond the horizon.
            </p>
          )}
        </div>
        <div className="oil-detail-table-block">
          <h4>Open POs</h4>
          {oil.open_pos.length === 0 ? <p className="oil-detail-empty">None</p> : (
            <table className="oil-mini-table">
              <thead><tr><th>PO</th><th>Ordered</th><th className="r">Remaining</th><th className="r">Amount</th></tr></thead>
              <tbody>
                {oil.open_pos.map((p) => (
                  <tr key={p.tranid + p.ordered} className={p.stale ? 'oil-row-stale' : ''}>
                    <td>{p.ns_url ? <a href={p.ns_url} target="_blank" rel="noreferrer">{p.tranid}</a> : p.tranid}</td>
                    <td>{fmtDate(p.ordered)}{p.age_days != null ? ` (${p.age_days}d)` : ''}</td>
                    <td className="r">{fmtLb(p.remaining_lb)} lb</td>
                    <td className="r">{fmtMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="oil-detail-table-block">
          <h4>Driving work orders</h4>
          {oil.driving_wos.length === 0 ? <p className="oil-detail-empty">None open</p> : (
            <table className="oil-mini-table">
              <thead><tr><th>WO</th><th>Due</th><th className="r">Qty</th></tr></thead>
              <tbody>
                {oil.driving_wos.slice(0, 8).map((w) => (
                  <tr key={w.tranid}>
                    <td>{w.ns_url ? <a href={w.ns_url} target="_blank" rel="noreferrer">{w.tranid}</a> : w.tranid}</td>
                    <td>{fmtDate(w.end_date)}</td>
                    <td className="r">{w.lb} lb</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="oil-detail-table-block">
          <h4>Uncovered sales orders</h4>
          {oil.driving_sos.length === 0 ? <p className="oil-detail-empty">All covered by stock / WOs</p> : (
            <table className="oil-mini-table">
              <thead><tr><th>SO</th><th>Customer</th><th>Ship</th><th className="r">Qty</th></tr></thead>
              <tbody>
                {oil.driving_sos.slice(0, 8).map((s, i) => (
                  <tr key={s.tranid + i}>
                    <td>{s.tranid}</td>
                    <td className="oil-mini-sku">{s.customer}</td>
                    <td>{fmtDate(s.ship_date)}</td>
                    <td className="r">{s.lb} lb</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {oil.other_locations.length > 0 && (
          <div className="oil-detail-table-block">
            <h4>Store-location stock <span className="oil-unverified-tag">counts unreliable</span></h4>
            <table className="oil-mini-table">
              <thead><tr><th>Location</th><th className="r">On hand</th></tr></thead>
              <tbody>
                {oil.other_locations.map((l) => (
                  <tr key={l.location}><td>{l.location}</td><td className="r">{fmtLb(l.lb)} lb</td></tr>
                ))}
              </tbody>
            </table>
            <p className="oil-detail-caption">Store counts are known-inaccurate and never feed production — informational only.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Weight draft-PO button: the existing native-first /api/oil-ordering path
// (used for BOTH oils and wax — identical lb/oz semantics).
function WeightDraftPoButton({ vendorId, vendorName, lines, onCreated }) {
  // idle -> armed (confirm) -> creating -> done | error
  const [state, setState] = useState('idle');
  const [result, setResult] = useState(null);
  if (!vendorId || lines.length === 0) return null;
  if (state === 'done' && result) {
    // Native-first server: no tranid + nativePoId means the PO was saved on
    // the native platform but the NetSuite copy is pending (NS was down; the
    // reverse-sync worker creates it later).
    if (!result.tranid && result.nativePoId) {
      return (
        <span className="oil-chip" title={result.ns_error || undefined}>
          Saved natively{result.native_po_number ? ` (${result.native_po_number})` : ''}
          {result.total ? ` · ${fmtMoney(result.total)}` : ''} — NetSuite copy pending, syncs automatically
        </span>
      );
    }
    return (
      <span className="oil-chip">
        Drafted {result.ns_url
          ? <a href={result.ns_url} target="_blank" rel="noreferrer">{result.tranid}</a>
          : result.tranid}{result.total ? ` · ${fmtMoney(result.total)}` : ''} — review &amp; send in NetSuite
      </span>
    );
  }
  const create = async () => {
    setState('creating');
    try {
      const res = await axios.post('/api/oil-ordering/draft-po', {
        vendor_id: vendorId, vendor_name: vendorName, lines,
      }, { timeout: 60000 });
      setResult(res.data);
      setState('done');
      if (onCreated) onCreated(res.data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || e.message });
      setState('error');
    }
  };
  if (state === 'error') {
    return <span className="oil-chip oil-chip-short" title={result?.error}>PO failed — {String(result?.error || '').slice(0, 80)} <button className="oil-link-btn" onClick={() => setState('idle')}>retry</button></span>;
  }
  return (
    <button
      className={`oil-draft-po-btn ${state === 'armed' ? 'armed' : ''}`}
      disabled={state === 'creating'}
      onClick={(e) => { e.stopPropagation(); state === 'armed' ? create() : setState('armed'); }}
      onBlur={() => state === 'armed' && setState('idle')}
      title={`Creates a draft PO in NetSuite (pending approval, not emailed): ${lines.length} item${lines.length > 1 ? 's' : ''}, ${Math.round(lines.reduce((s, l) => s + l.lb, 0))} lb`}
    >
      {state === 'creating' ? 'Creating…' : state === 'armed' ? 'Confirm draft PO?' : 'Draft PO'}
    </button>
  );
}

function VendorOrderSummary({ oils }) {
  // Roll the suggested buys up per vendor, check vendor order minimums, and
  // offer a one-click draft PO in NetSuite.
  const byVendor = new Map();
  for (const x of oils) {
    if (!x.suggest_lb) continue;
    const v = byVendor.get(x.vendor_name) || { id: x.vendor_id, lb: 0, usd: 0, minUsd: x.vendor_order_min_usd, lines: [] };
    v.lb += x.suggest_lb;
    v.usd += x.suggest_lb * (x.cost_per_lb || 0);
    v.lines.push({ oil_id: x.oil_id, lb: x.suggest_lb, sku: x.sku, cost_per_lb: x.cost_per_lb || 0 });
    byVendor.set(x.vendor_name, v);
  }
  if (byVendor.size === 0) return null;
  return (
    <div className="oil-chip-cell oil-mini-pad" style={{ marginTop: 4 }}>
      {[...byVendor.entries()].map(([name, v]) => {
        const belowMin = v.minUsd && v.usd < v.minUsd;
        return (
          <span key={name} className={`oil-chip ${belowMin ? 'oil-chip-short' : ''}`}>
            {trimVendor(name)} order: {fmtLb(v.lb)} lb / ~{fmtMoney(v.usd)} ({v.lines.length} item{v.lines.length > 1 ? 's' : ''})
            {belowMin ? ` — below ${fmtMoney(v.minUsd)} vendor minimum, pad the order` : ''}
            {' '}<WeightDraftPoButton vendorId={v.id} vendorName={name} lines={v.lines} />
          </span>
        );
      })}
    </div>
  );
}

function WeightPoBuilder({ oils, recKey }) {
  // Shortage-wide per-vendor order builder: every item with a suggested buy.
  const byVendor = new Map();
  for (const o of oils) {
    const rec = o[recKey];
    if (!rec || !o.vendor_id) continue;
    const v = byVendor.get(o.vendor_name) || { id: o.vendor_id, lb: 0, usd: 0, minUsd: o.vendor_order_min_usd, lines: [] };
    v.lb += rec;
    v.usd += rec * (o.cost_per_lb || 0);
    v.lines.push({ oil_id: o.id, lb: rec, sku: o.sku, cost_per_lb: o.cost_per_lb || 0 });
    byVendor.set(o.vendor_name, v);
  }
  if (byVendor.size === 0) return null;
  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>PO builder</h3>
        <span className="oil-panel-sub">suggested buys rolled up per vendor — drafts a real NetSuite PO (pending approval, not emailed)</span>
      </div>
      <div className="oil-chip-cell" style={{ padding: '0 16px 14px' }}>
        {[...byVendor.entries()].sort((a, b) => b[1].usd - a[1].usd).map(([name, v]) => {
          const belowMin = v.minUsd && v.usd < v.minUsd;
          return (
            <span key={name} className={`oil-chip ${belowMin ? 'oil-chip-short' : ''}`}>
              {trimVendor(name)}: {v.lines.length} item{v.lines.length > 1 ? 's' : ''} · {fmtLb(v.lb)} lb · ~{fmtMoney(v.usd)}
              {belowMin ? ` — below ${fmtMoney(v.minUsd)} min` : ''}
              {' '}<WeightDraftPoButton vendorId={v.id} vendorName={name} lines={v.lines} />
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Customer production watch — OILS TAB ONLY (fed by the oils engine payload,
// which owns named-customer WO tracking).
function CommittedOrders({ orders }) {
  const [expanded, setExpanded] = useState(null);
  const [showCovered, setShowCovered] = useState(false);
  const visible = orders.filter((o) => showCovered || o.status !== 'COVERED');
  const coveredCount = orders.filter((o) => o.status === 'COVERED').length;
  if (orders.length === 0) return null;
  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>Customer production watch</h3>
        <span className="oil-panel-sub">open work orders for named customers — oil must be ordered by the date shown</span>
        {coveredCount > 0 && (
          <button className="oil-link-btn" onClick={() => setShowCovered((s) => !s)}>
            {showCovered ? 'hide' : 'show'} {coveredCount} covered
          </button>
        )}
      </div>
      <table className="oil-list-table">
        <thead>
          <tr><th>Status</th><th>Customer</th><th className="r">WOs</th><th className="r">Oil needed</th><th>Due</th><th>Order oil by</th><th>Oils</th></tr>
        </thead>
        <tbody>
          {visible.map((o) => {
            const isOpen = expanded === o.customer;
            const meta = ORDER_STATUS_META[o.status] || ORDER_STATUS_META.COVERED;
            const due = o.due_first === o.due_last ? fmtDate(o.due_first)
              : `${fmtDate(o.due_first)} → ${fmtDate(o.due_last)}`;
            return (
              <React.Fragment key={o.customer}>
                <tr className="oil-list-row" onClick={() => setExpanded(isOpen ? null : o.customer)}>
                  <td><span className={`oil-pill ${meta.cls}`}>{meta.label}</span></td>
                  <td className="oil-cust">{o.customer}</td>
                  <td className="r">{o.wo_count}</td>
                  <td className="r">{fmtLb(o.total_oil_lb)} lb</td>
                  <td>{due}</td>
                  <td className={o.status === 'LATE' ? 'oil-num-short' : ''}>{fmtDate(o.order_by)}</td>
                  <td className="oil-chip-cell">
                    {o.oils.slice(0, 4).map((x) => (
                      <span key={x.sku} className={`oil-chip ${x.short_lb > 0 ? 'oil-chip-short' : ''}`}>
                        {shortSku(x.sku)}{x.short_lb > 0 ? ` −${fmtLb(x.short_lb)}` : ''}
                      </span>
                    ))}
                    {o.oils.length > 4 && <span className="oil-chip">+{o.oils.length - 4}</span>}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="oil-detail-row"><td colSpan={7}>
                    <table className="oil-mini-table oil-mini-pad">
                      <thead><tr><th>Oil</th><th className="r">On hand</th><th className="r" title="Oil these customer WOs need">This order</th><th className="r" title="All known demand: horizon (WO+SO vs seasonal) + future committed orders">Total needed</th><th>Vendor</th><th className="r">$/lb</th><th>Order by</th><th>Status</th></tr></thead>
                      <tbody>
                        {o.oils.map((x) => (
                          <tr key={x.sku}>
                            <td>{x.sku}</td>
                            <td className={`r ${x.prod_onhand_lb < 1 ? 'oil-num-zero' : ''}`}>{fmtLb(x.prod_onhand_lb)} lb</td>
                            <td className="r">{fmtLb(x.need_lb)} lb</td>
                            <td className="r">{fmtLb(x.total_needed_lb)} lb</td>
                            <td className="oil-mini-sku">{trimVendor(x.vendor_name)}</td>
                            <td className="r">{x.cost_per_lb > 0 ? `$${x.cost_per_lb.toFixed(2)}` : '—'}</td>
                            <td>{fmtDate(x.order_by)}</td>
                            <td><span className={`oil-pill ${(ORDER_STATUS_META[x.status] || {}).cls || 'oil-pill-ok'}`}>{(ORDER_STATUS_META[x.status] || {}).label || x.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <VendorOrderSummary oils={o.oils} />
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Customer production watch for EACH types (vessels) — same idea as the oils
// watch: open named-customer WOs, the WO's own component lines are the
// requirement, coverage vs mirror on-hand net of everyone else's demand.
function EachCommittedOrders({ orders, noun }) {
  const [expanded, setExpanded] = useState(null);
  const [showCovered, setShowCovered] = useState(false);
  if (!orders || orders.length === 0) return null;
  const visible = orders.filter((o) => showCovered || o.status !== 'COVERED');
  const coveredCount = orders.filter((o) => o.status === 'COVERED').length;
  const vendorRollup = (items) => {
    const byVendor = new Map();
    for (const x of items) {
      if (!(x.suggest > 0)) continue;
      const key = x.vendor_name || 'No vendor';
      const v = byVendor.get(key) || { vendorNsId: x.vendor_ns_id, usd: 0, lines: [] };
      if (!v.vendorNsId && x.vendor_ns_id) v.vendorNsId = x.vendor_ns_id;
      v.usd += x.suggest * (x.unit_cost || 0);
      v.lines.push({ sku: x.sku, quantity: x.suggest, unitCost: x.unit_cost || 0, isWeight: x.is_weight === true });
      byVendor.set(key, v);
    }
    return byVendor;
  };
  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>Customer production watch</h3>
        <span className="oil-panel-sub">open work orders for named customers — {noun} must be ordered by the date shown</span>
        {coveredCount > 0 && (
          <button className="oil-link-btn" onClick={() => setShowCovered((s) => !s)}>
            {showCovered ? 'hide' : 'show'} {coveredCount} covered
          </button>
        )}
      </div>
      <table className="oil-list-table">
        <thead>
          <tr><th>Status</th><th>Customer</th><th className="r">WOs</th><th className="r">{noun[0].toUpperCase() + noun.slice(1)} needed</th><th>Due</th><th>Order by</th><th>Items</th></tr>
        </thead>
        <tbody>
          {visible.map((o) => {
            const isOpen = expanded === o.customer;
            const meta = ORDER_STATUS_META[o.status] || ORDER_STATUS_META.COVERED;
            const due = o.due_first === o.due_last ? fmtDate(o.due_first)
              : `${fmtDate(o.due_first)} → ${fmtDate(o.due_last)}`;
            const byVendor = isOpen ? vendorRollup(o.items) : null;
            return (
              <React.Fragment key={o.customer}>
                <tr className="oil-list-row" onClick={() => setExpanded(isOpen ? null : o.customer)}>
                  <td><span className={`oil-pill ${meta.cls}`}>{meta.label}</span></td>
                  <td className="oil-cust">{o.customer}</td>
                  <td className="r">{o.wo_count}</td>
                  <td className="r">{fmtQty(o.total_units)}</td>
                  <td>{due}</td>
                  <td className={o.status === 'LATE' ? 'oil-num-short' : ''}>{fmtDate(o.order_by)}</td>
                  <td className="oil-chip-cell">
                    {o.items.slice(0, 4).map((x) => (
                      <span key={x.sku} className={`oil-chip ${x.short > 0 ? 'oil-chip-short' : ''}`}>
                        {shortSku(x.sku)}{x.short > 0 ? ` −${fmtQty(x.short)}` : ''}
                      </span>
                    ))}
                    {o.items.length > 4 && <span className="oil-chip">+{o.items.length - 4}</span>}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="oil-detail-row"><td colSpan={7}>
                    <table className="oil-mini-table oil-mini-pad">
                      <thead><tr><th>Item</th><th className="r">On hand</th><th className="r">On order</th><th className="r" title={`${noun} these customer WOs need`}>This order</th><th className="r">Short</th><th>Vendor</th><th className="r">Unit cost</th><th>Order by</th><th>Status</th></tr></thead>
                      <tbody>
                        {o.items.map((x) => (
                          <tr key={x.sku}>
                            <td>{x.sku}</td>
                            <td className={`r ${x.onhand < 1 ? 'oil-num-zero' : ''}`}>{fmtQty(x.onhand)}</td>
                            <td className="r">{fmtQty(x.on_order)}</td>
                            <td className="r">{fmtQty(x.need)}</td>
                            <td className={`r ${x.short > 0 ? 'oil-num-short' : ''}`}>{x.short > 0 ? fmtQty(x.short) : '—'}</td>
                            <td className="oil-mini-sku">{trimVendor(x.vendor_name || '')}</td>
                            <td className="r">{x.unit_cost > 0 ? `$${x.unit_cost.toFixed(2)}` : '—'}</td>
                            <td>{fmtDate(x.order_by)}</td>
                            <td><span className={`oil-pill ${(ORDER_STATUS_META[x.status] || {}).cls || 'oil-pill-ok'}`}>{(ORDER_STATUS_META[x.status] || {}).label || x.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {byVendor && byVendor.size > 0 && (
                      <div className="oil-chip-cell oil-mini-pad" style={{ marginTop: 4 }}>
                        {[...byVendor.entries()].map(([name, v]) => (
                          <span key={name} className="oil-chip">
                            {trimVendor(name)} order: {fmtQty(v.lines.reduce((s, l) => s + l.quantity, 0))} units / ~{fmtMoney(v.usd)}
                            {' '}<EachDraftPoButton vendorNsId={v.vendorNsId} vendorName={name} lines={v.lines} />
                          </span>
                        ))}
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OpenPoList({ pos }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const impactful = pos.filter((p) => p.shortfall_cured_lb > 0);
  const totalValue = pos.reduce((s, p) => s + p.total_amount, 0);
  if (pos.length === 0) return null;
  return (
    <div className="oil-panel">
      <button className="oil-panel-toggle" onClick={() => setOpen((s) => !s)}>
        <span className="oil-panel-caret">{open ? '▾' : '▸'}</span>
        Open POs — {pos.length} totaling {fmtMoney(totalValue)}
      </button>
      {open && (
        <table className="oil-list-table">
          <thead>
            <tr><th>PO</th><th>Vendor</th><th>Ordered</th><th className="r">Amount</th><th className="r">Would cure</th><th>Covers</th></tr>
          </thead>
          <tbody>
            {impactful.map((p) => {
              const isOpen = expanded === p.tranid;
              return (
                <React.Fragment key={p.tranid}>
                  <tr className="oil-list-row" onClick={() => setExpanded(isOpen ? null : p.tranid)}>
                    <td>{p.ns_url ? <a href={p.ns_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{p.tranid}</a> : p.tranid}</td>
                    <td className="oil-cust">{p.vendor_name}</td>
                    <td>{fmtDate(p.ordered)}{p.age_days > 45 ? <span className="oil-num-short"> · {p.age_days}d old</span> : ''}</td>
                    <td className="r">{fmtMoney(p.total_amount)}</td>
                    <td className="r"><strong>{fmtLb(p.shortfall_cured_lb)} lb</strong></td>
                    <td className="oil-chip-cell">
                      {p.top_lines.slice(0, 3).map((l) => <span key={l.sku} className="oil-chip">{shortSku(l.sku)} {fmtLb(l.cures_lb)}</span>)}
                      {p.top_lines.length > 3 && <span className="oil-chip">+{p.top_lines.length - 3}</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="oil-detail-row"><td colSpan={6}>
                      <div className="oil-chip-cell oil-mini-pad">
                        {p.top_lines.map((l) => <span key={l.sku} className="oil-chip">{l.sku}: cures {fmtLb(l.cures_lb)} lb</span>)}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WeightTab({ typeKey, data, loading, error, horizon, onHorizonChange, onRefresh }) {
  const typeLabel = data?.type_label || (typeKey === 'wax' ? 'wax' : 'oil');
  const plural = typeKey === 'wax' ? 'waxes' : `${typeLabel}s`;
  const [vendor, setVendor] = useState('all');
  const [brand, setBrand] = useState('all');
  const [onlyAction, setOnlyAction] = useState(true);
  const [creditPos, setCreditPos] = useState(false);
  const [view, setView] = useState('short'); // 'short' | 'jit'
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('shortfall_lb');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(null);

  const shortKey = creditPos ? 'shortfall_after_po_lb' : 'shortfall_lb';
  const recKey = creditPos ? 'recommend_after_po_lb' : 'recommend_lb';

  const vendors = useMemo(() => {
    if (!data) return [];
    return [...new Set((data.oils || []).map((o) => o.vendor_name).filter(Boolean))].sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    let rows = (data.oils || []).filter((o) =>
      (vendor === 'all' || o.vendor_name === vendor) &&
      (brand === 'all' || o.brand === brand) &&
      (view === 'short'
        ? (!onlyAction || o.flags.some((f) => ACTION_FLAGS.includes(f)))
        : (!onlyAction || o.is_dead || o.is_overstock)) &&
      (!q || o.sku.toLowerCase().includes(q) || (o.name || '').toLowerCase().includes(q))
    );
    const key = sortKey === 'shortfall_lb' ? shortKey : sortKey;
    rows = [...rows].sort((a, b) => {
      const av = a[key] ?? -Infinity;
      const bv = b[key] ?? -Infinity;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [data, vendor, brand, onlyAction, search, sortKey, sortDir, shortKey, view]);

  const kpis = useMemo(() => {
    if (!data) return null;
    const scope = (data.oils || []).filter((o) => (vendor === 'all' || o.vendor_name === vendor) && (brand === 'all' || o.brand === brand));
    const short = scope.reduce((s, o) => s + (o[shortKey] || 0), 0);
    const needAction = scope.filter((o) => o.flags.some((f) => ['ORDER_NOW', 'VERIFY_PO', 'OUT_AT_PRODUCTION'].includes(f))).length;
    const commitRisk = (data.committed_orders || []).filter((o) => o.status === 'LATE' || o.status === 'AT_RISK').length;
    const poValue = scope.reduce((s, o) => s + o.open_pos.reduce((t, p) => t + (p.amount || 0), 0), 0);
    return { short, needAction, commitRisk, poValue };
  }, [data, vendor, brand, shortKey]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(['sku', 'vendor_name', 'brand'].includes(key) ? 'asc' : 'desc'); }
  };

  const switchView = (v) => {
    setView(v);
    setExpanded(null);
    setSortKey(v === 'jit' ? 'excess_value' : 'shortfall_lb');
    setSortDir('desc');
  };

  if (loading && !data) {
    return (
      <div>
        <LoadingSpinner />
        <p className="oil-loading-note">Computing demand across NetSuite work orders, sales orders and build history — first load takes about a minute.</p>
      </div>
    );
  }
  if (error) return <div className="message-box error">{error}</div>;
  if (!data) return null;

  return (
    <>
      <div className="oil-top">
        {view === 'short' && kpis && (
          <div className="oil-kpi-strip" style={{ marginLeft: 0 }}>
            <div className="oil-kpi"><span className="oil-kpi-value">{kpis.needAction}</span><span className="oil-kpi-label">{plural} need action</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtLb(kpis.short)} lb</span><span className="oil-kpi-label">shortfall{creditPos ? ' (POs credited)' : ''}</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{kpis.commitRisk}</span><span className="oil-kpi-label">big orders at risk</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(kpis.poValue)}</span><span className="oil-kpi-label">open POs</span></div>
            {data?.trend && <div className="oil-kpi" title={`Base-business consumption, trailing 6 complete months vs same months last year: ${data.trend.current_6mo_lb} lb vs ${data.trend.prior_6mo_lb} lb (raw ×${data.trend.raw}, clamped 0.5–1.5). Scales every ${typeLabel}'s last-year window.`}><span className="oil-kpi-value">×{data.trend.factor}</span><span className="oil-kpi-label">6-mo YoY trend</span></div>}
          </div>
        )}
        {view === 'jit' && data?.jit && (
          <div className="oil-kpi-strip" style={{ marginLeft: 0 }}>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(data.jit.stock_value)}</span><span className="oil-kpi-label">{typeLabel} at production</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(data.jit.excess_value)}</span><span className="oil-kpi-label">excess vs JIT target</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{data.jit.overstock_count}</span><span className="oil-kpi-label">overstocked {plural}</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{data.jit.dead_count} · {fmtMoney(data.jit.dead_value)}</span><span className="oil-kpi-label">dead stock</span></div>
          </div>
        )}
        <div className="oil-header-controls" style={{ marginLeft: 'auto' }}>
          <select className="oil-select" value={horizon} onChange={(e) => onHorizonChange(Number(e.target.value))}>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button className="low-stock-refresh" onClick={onRefresh}>Refresh</button>
        </div>
      </div>

      <details className="oil-how">
        <summary>How these numbers work</summary>
        <p>
          Demand = {typeLabel} on open work orders within the horizon, plus unshipped sales orders not already covered by
          finished-goods stock or an open WO (FIFO by ship date), compared against the same window last year scaled by the overall 6-month year-over-year base-business trend (base business only — last year's B&N/private-label program builds are excluded, since this year's programs are counted from their own work orders) — whichever
          is larger. Supply counts <strong>production (Rewined) stock only</strong>; store counts are unreliable and never
          feed builds. Open POs are <strong>not</strong> credited against shortfall by default (prepay vendors — a PO isn't
          supply until it's paid); toggle "credit open POs" to include them. The customer production
          watch (top of the Oils tab) tracks open work orders carrying a named customer (make-to-order programs, incl. planned WOs): the WO's
          own component lines are the requirement, and order-by = WO start date − vendor lead time. The Overstock / JIT view
          compares production stock to a just-in-time target (burn over vendor lead time + {data?.jit_safety_days || 30}d
          safety, using the fastest of trailing-12mo / committed / last-year pace so pre-season stocking isn't flagged);
          excess is valued at NetSuite average cost. All quantities shown in pounds — NetSuite stores every {typeLabel} quantity
          in its base unit (ounces), converted here ÷16.
        </p>
      </details>

      {view === 'short' && <WeightPoBuilder oils={data?.oils || []} recKey={recKey} />}
      {view === 'short' && <OpenPoList pos={data?.pay_priority || []} />}

      <div className="oil-controls">
        <div className="oil-brand-toggle">
          <button className={`oil-brand-btn ${view === 'short' ? 'active' : ''}`} onClick={() => switchView('short')}>Shortages</button>
          <button className={`oil-brand-btn ${view === 'jit' ? 'active' : ''}`} onClick={() => switchView('jit')}>Overstock / JIT</button>
        </div>
        <select className="oil-select" value={vendor} onChange={(e) => setVendor(e.target.value)}>
          <option value="all">All vendors</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <div className="oil-brand-toggle">
          {['all', 'Rewined', 'Candlefish', 'Other'].map((b) => (
            <button key={b} className={`oil-brand-btn ${brand === b ? 'active' : ''}`} onClick={() => setBrand(b)}>
              {b === 'all' ? 'All brands' : b}
            </button>
          ))}
        </div>
        <label className="oil-check">
          <input type="checkbox" checked={onlyAction} onChange={(e) => setOnlyAction(e.target.checked)} />
          {view === 'short' ? 'Needs attention' : 'Excess only'}
        </label>
        {view === 'short' && (
          <label className="oil-check" title="Treat open POs as incoming supply">
            <input type="checkbox" checked={creditPos} onChange={(e) => setCreditPos(e.target.checked)} />
            Credit open POs
          </label>
        )}
        <input className="low-stock-search" type="text" placeholder={`Search ${typeLabel}...`} value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="oil-count">{filtered.length} {plural}</span>
      </div>

      {view === 'jit' && (
        <div className="low-stock-table-wrap">
          <table className="low-stock-table oil-table">
            <thead>
              <tr>
                <Th label="Item" col="sku" {...{ sortKey, sortDir, handleSort }} />
                <Th label="Popularity" col="popularity_rev_ttm" {...{ sortKey, sortDir, handleSort }} align="right" />
                <Th label="Prod stock" col="onhand_prod_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="On hand at Rewined production locations (lb)" />
                <Th label="Use / mo" col="avg_monthly_burn_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="max(trailing-12mo avg, committed pace, last-year pace) — seasonal-aware" />
                <Th label="Cover" col="cover_months" {...{ sortKey, sortDir, handleSort }} align="right" title="Months of production stock at that pace" />
                <Th label="JIT target" col="jit_target_lb" {...{ sortKey, sortDir, handleSort }} align="right" title={`Burn over vendor lead time + ${data?.jit_safety_days || 30}d safety`} />
                <Th label="Excess lb" col="excess_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Stock beyond max(JIT target, near-term demand)" />
                <Th label="$/lb" col="cost_per_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="NetSuite average cost" />
                <Th label="Excess $" col="excess_value" {...{ sortKey, sortDir, handleSort }} align="right" />
                <th className="low-stock-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const isOpen = expanded === o.id;
                return (
                  <React.Fragment key={o.id}>
                    <tr className={`oil-row ${isOpen ? 'oil-row-open' : ''}`} onClick={() => setExpanded(isOpen ? null : o.id)}>
                      <td className="oil-cell-sku">
                        <span className={`oil-brand-dot oil-brand-${o.brand.toLowerCase()}`} title={o.brand} />
                        <span className="oil-sku-block">
                          {o.ns_url
                            ? <a href={o.ns_url} target="_blank" rel="noreferrer" className="low-stock-ns-link" onClick={(e) => e.stopPropagation()}>{o.sku}</a>
                            : o.sku}
                          <span className="oil-sku-vendor">{trimVendor(o.vendor_name)}</span>
                        </span>
                      </td>
                      <td className="low-stock-num">
                        {o.popularity_rank ? <span className="oil-pop-rank">#{o.popularity_rank}</span> : ''}
                        {o.popularity_rev_ttm > 0 ? ` ${fmtMoney(o.popularity_rev_ttm)}` : '—'}
                      </td>
                      <td className="low-stock-num">{fmtLb(o.onhand_prod_lb)}</td>
                      <td className="low-stock-num">{o.avg_monthly_burn_lb > 0 ? fmtLb(o.avg_monthly_burn_lb) : '—'}</td>
                      <td className="low-stock-num">{o.cover_months != null ? `${Math.round(o.cover_months)}mo` : (o.onhand_prod_lb > 1 ? '∞' : '—')}</td>
                      <td className="low-stock-num oil-num-dim">{o.jit_target_lb > 0 ? fmtLb(o.jit_target_lb) : '—'}</td>
                      <td className={`low-stock-num ${o.excess_lb > 25 ? 'oil-num-short' : ''}`}>{o.excess_lb > 0 ? fmtLb(o.excess_lb) : '—'}</td>
                      <td className="low-stock-num oil-num-dim">{o.cost_per_lb > 0 ? `$${o.cost_per_lb.toFixed(2)}` : '—'}</td>
                      <td className="low-stock-num oil-num-rec">{o.excess_value > 0 ? fmtMoney(o.excess_value) : '—'}</td>
                      <td className="oil-pills">
                        {o.is_dead && <span className="oil-pill oil-pill-serious">Dead stock</span>}
                        {o.is_overstock && <span className="oil-pill oil-pill-warning">Overstock</span>}
                        {o.drum_exceeds_use && (o.is_overstock || o.is_dead) && (
                          <span className="oil-pill oil-pill-muted" title={`Burns ${o.burn_12mo_lb} lb/yr but minimum order is ${o.order_increment_lb} lb — every reorder guarantees over a year of supply`}>
                            Min order &gt; use
                          </span>
                        )}
                        {!o.is_dead && !o.is_overstock && <span className="oil-pill oil-pill-ok">Lean</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="oil-detail-row">
                        <td colSpan={10}>
                          <WeightDetail oil={o} horizonDays={data?.horizon_days || horizon} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="no-data">No items match the current filters.</div>}
        </div>
      )}

      {view === 'short' && (
      <div className="low-stock-table-wrap">
        <table className="low-stock-table oil-table">
          <thead>
            <tr>
              <Th label="Item" col="sku" {...{ sortKey, sortDir, handleSort }} />
              <Th label="Popularity" col="popularity_rev_ttm" {...{ sortKey, sortDir, handleSort }} align="right" title="TTM finished-good revenue attributed to this item (rank within brand)" />
              <Th label="Prod stock" col="onhand_prod_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="On hand at Rewined production locations (lb)" />
              <Th label="WO" col="wo_committed_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Quantity on open work orders in horizon (lb)" />
              <Th label="SO" col="so_residual_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Unshipped SO demand in horizon not covered by FG stock or WOs (lb)" />
              <Th label="Future 500+" col="future_commit_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Large committed orders shipping beyond the horizon (lb)" />
              <Th label="On order" col="on_order_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Open external-vendor POs" />
              <Th label="Short" col="shortfall_lb" {...{ sortKey, sortDir, handleSort }} align="right" title={creditPos ? 'max(committed, base-seasonal + program WOs) − prod stock − on order' : 'max(committed, base-seasonal + program WOs) − prod stock (POs NOT credited)'} />
              <Th label="Order" col="recommend_lb" {...{ sortKey, sortDir, handleSort }} align="right" title="Suggested order, rounded to vendor increment" />
              <Th label="Cover" col="weeks_cover" {...{ sortKey, sortDir, handleSort }} align="right" title="Weeks of production stock at max(committed, seasonal) pace" />
              <th className="low-stock-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => {
              const isOpen = expanded === o.id;
              const short = o[shortKey];
              const rec = o[recKey];
              const coverCls = o.weeks_cover == null ? '' : o.weeks_cover < 2 ? 'oil-cover-critical' : o.weeks_cover < 4 ? 'oil-cover-low' : '';
              return (
                <React.Fragment key={o.id}>
                  <tr className={`oil-row ${isOpen ? 'oil-row-open' : ''}`} onClick={() => setExpanded(isOpen ? null : o.id)}>
                    <td className="oil-cell-sku">
                      <span className={`oil-brand-dot oil-brand-${o.brand.toLowerCase()}`} title={o.brand} />
                      <span className="oil-sku-block">
                        {o.ns_url
                          ? <a href={o.ns_url} target="_blank" rel="noreferrer" className="low-stock-ns-link" onClick={(e) => e.stopPropagation()}>{o.sku}</a>
                          : o.sku}
                        <span className="oil-sku-vendor">{trimVendor(o.vendor_name)}</span>
                      </span>
                    </td>
                    <td className="low-stock-num">
                      {o.popularity_rank ? <span className="oil-pop-rank">#{o.popularity_rank}</span> : ''}
                      {o.popularity_rev_ttm > 0 ? ` ${fmtMoney(o.popularity_rev_ttm)}` : '—'}
                    </td>
                    <td className={`low-stock-num ${o.onhand_prod_lb < 1 ? 'oil-num-zero' : ''}`}>{fmtLb(o.onhand_prod_lb)}</td>
                    <td className="low-stock-num">{o.wo_committed_lb > 0 ? fmtLb(o.wo_committed_lb) : '—'}</td>
                    <td className="low-stock-num">{o.so_residual_lb > 0 ? fmtLb(o.so_residual_lb) : '—'}</td>
                    <td className="low-stock-num">{o.future_commit_lb > 0 ? fmtLb(o.future_commit_lb) : '—'}</td>
                    <td className="low-stock-num oil-num-dim">{o.on_order_lb > 0 ? fmtLb(o.on_order_lb) : '—'}</td>
                    <td className={`low-stock-num ${short > 0 ? 'oil-num-short' : ''}`}>{short > 0 ? fmtLb(short) : '—'}</td>
                    <td className="low-stock-num oil-num-rec">{rec > 0 ? fmtLb(rec) : '—'}</td>
                    <td className={`low-stock-num ${coverCls}`}>{o.weeks_cover != null ? `${o.weeks_cover}w` : '—'}</td>
                    <td className="oil-pills">
                      {o.flags.filter((f) => f !== 'OK').slice(0, 2).map((f) => (
                        <span key={f} className={`oil-pill ${FLAG_META[f]?.cls || ''}`}>{FLAG_META[f]?.label || f}</span>
                      ))}
                      {o.flags.includes('OK') && <span className="oil-pill oil-pill-ok">OK</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="oil-detail-row">
                      <td colSpan={11}>
                        <WeightDetail oil={o} horizonDays={data?.horizon_days || horizon} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="no-data">No items match the current filters.</div>}
      </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// BUDGET PLANNER — "I have $X to spend: what are the most critical buys?"
// Pulls every tab's payload (cheap once the server cache is warm), ranks ALL
// suggested buys across material types by urgency, and walks the budget down
// the list:
//   1. Blocking production — out at production with committed WO demand
//   2. Order date passed   — order-by is today or earlier
//   3. Order within 14 days
//   4. Upcoming            — everything else with a suggested buy
// Within a tier: earlier order-by first, then larger committed demand. Items
// whose shortage already has an unverified open PO (VERIFY_PO) rank behind
// clean buys in their tier — verify/pay that PO before spending new money.
// Partial buys are allowed when the full quantity doesn't fit, snapped DOWN
// to the item's order increment. Per-vendor draft buttons reuse the existing
// native-first PO paths (weight → oil engine, each → materials engine).
// ════════════════════════════════════════════════════════════════════════════

const TAB_LABEL = Object.fromEntries(TABS.map((t) => [t.key, t.label]));
const PLAN_TIERS = ['Blocking production', 'Order date passed', 'Order within 14 days', 'Upcoming'];

function buildBudgetCandidates(dataByType) {
  const out = [];
  for (const t of TABS) {
    const data = dataByType[t.key];
    if (!data) continue;
    if (t.kind === 'weight') {
      for (const o of data.oils || []) {
        const qty = o.recommend_lb || 0;
        if (qty <= 0) continue;
        out.push({
          key: `${t.key}:${o.sku}`, type: t.key, kind: 'weight',
          id: o.id, sku: o.sku, name: o.name,
          vendorKey: `${o.vendor_name || 'No vendor'}|weight`,
          vendorName: o.vendor_name || 'No vendor', vendorId: o.vendor_id || null,
          unitCost: o.cost_per_lb || 0, maxQty: qty,
          increment: Math.max(1, o.order_increment_lb || 1),
          orderBy: o.order_by_date || null,
          isOut: o.flags.includes('OUT_AT_PRODUCTION'),
          verifyPo: o.flags.includes('VERIFY_PO'),
          committed: (o.wo_committed_lb || 0) + (o.so_residual_lb || 0),
          unit: 'lb',
        });
      }
    } else {
      for (const r of data.rows || []) {
        const qty = r.suggestedBuy || 0;
        if (qty <= 0) continue;
        out.push({
          key: `${t.key}:${r.sku}`, type: t.key, kind: 'each',
          sku: r.sku, name: r.name,
          vendorKey: `${r.vendorName || 'No vendor'}|each`,
          vendorName: r.vendorName || 'No vendor', vendorNsId: r.vendorNsId || null,
          unitCost: r.lastCost || 0, maxQty: qty,
          increment: Math.max(1, r.reorderMultiple || 1),
          orderBy: r.orderBy || null,
          isOut: r.flags.includes('OUT'),
          verifyPo: false,
          committed: r.woCommitted || 0,
          isWeight: r.isWeight === true,
          unit: r.isWeight === true ? 'lb' : 'units',
        });
      }
    }
  }
  return out;
}

function computeBudgetPlan(budget, dataByType) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const soonIso = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const candidates = buildBudgetCandidates(dataByType);
  for (const c of candidates) {
    c.tier = c.isOut && c.committed > 0 ? 0
      : c.orderBy && c.orderBy <= todayIso ? 1
      : c.orderBy && c.orderBy <= soonIso ? 2
      : 3;
  }
  candidates.sort((a, b) =>
    (a.tier - b.tier) ||
    ((a.verifyPo ? 1 : 0) - (b.verifyPo ? 1 : 0)) ||
    ((a.orderBy || '9999') < (b.orderBy || '9999') ? -1 : (a.orderBy || '9999') > (b.orderBy || '9999') ? 1 : 0) ||
    (b.committed - a.committed));

  let remaining = budget;
  const taken = [];
  const skipped = [];
  const noCost = [];
  for (const c of candidates) {
    if (!(c.unitCost > 0)) { noCost.push(c); continue; }
    let qty = c.maxQty;
    if (qty * c.unitCost > remaining) {
      qty = Math.min(qty, Math.floor(remaining / c.unitCost / c.increment) * c.increment);
    }
    if (qty > 0) {
      const cost = qty * c.unitCost;
      taken.push({ ...c, qty, cost, partial: qty < c.maxQty });
      remaining -= cost;
    } else {
      skipped.push(c);
    }
  }
  const unmetUsd = skipped.reduce((s, c) => s + c.maxQty * c.unitCost, 0) +
    taken.filter((l) => l.partial).reduce((s, l) => s + (l.maxQty - l.qty) * l.unitCost, 0);
  return { taken, skipped, noCost, spent: budget - remaining, remaining, unmetUsd };
}

function BudgetPlanner({ activeTab, dataByType, errorByType, loadingByType, fetchType }) {
  const [input, setInput] = useState('');
  const [budget, setBudget] = useState(null);
  const [inputError, setInputError] = useState(false);
  // The budget plans within ONE product type ("I have $3k for labels") —
  // never across all types (Adam 7/13/26). Follows the open tab until a plan
  // is running; the chips switch it (and re-plan the same budget) any time.
  const [scope, setScope] = useState(activeTab);
  useEffect(() => {
    if (!budget) setScope(activeTab);
  }, [activeTab, budget]);
  const settled = dataByType[scope] || errorByType[scope];
  const failed = !dataByType[scope] && errorByType[scope];

  // Once a plan is requested, pull the scoped tab's payload if we don't have
  // it yet (served from the warm server cache, so this is normally instant).
  useEffect(() => {
    if (!budget) return;
    if (!dataByType[scope] && !errorByType[scope] && !loadingByType[scope]) fetchType(scope);
  }, [budget, scope, dataByType, errorByType, loadingByType, fetchType]);

  const plan = useMemo(() => {
    if (!budget || !dataByType[scope]) return null;
    return computeBudgetPlan(budget, { [scope]: dataByType[scope] });
  }, [budget, dataByType, scope]);

  const start = () => {
    const b = Math.round(parseFloat(String(input).replace(/[^0-9.]/g, '')));
    if (b > 0) {
      setInputError(false);
      setBudget(b);
    } else {
      // Empty/invalid input must not be a silent no-op — the "3000"
      // placeholder makes the field look filled.
      setInputError(true);
    }
  };

  const byVendor = useMemo(() => {
    if (!plan) return new Map();
    const map = new Map();
    for (const l of plan.taken) {
      const v = map.get(l.vendorKey) ||
        { kind: l.kind, vendorName: l.vendorName, vendorId: l.vendorId, vendorNsId: l.vendorNsId, usd: 0, lines: [] };
      v.usd += l.cost;
      v.lines.push(l.kind === 'weight'
        ? { oil_id: l.id, lb: l.qty, sku: l.sku, cost_per_lb: l.unitCost }
        : { sku: l.sku, quantity: l.qty, unitCost: l.unitCost, isWeight: l.isWeight === true });
      map.set(l.vendorKey, v);
    }
    return map;
  }, [plan]);

  let running = 0;
  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>Budget planner</h3>
        <span className="oil-panel-sub">enter what you can spend on a product type — ranks that type's suggested buys by urgency and fills the budget top-down</span>
      </div>
      <div className="budget-input-row">
        <span className="budget-currency">$</span>
        <input
          className="low-stock-search budget-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 3000"
          value={input}
          onChange={(e) => { setInput(e.target.value); setInputError(false); }}
          onKeyDown={(e) => e.key === 'Enter' && start()}
          style={inputError ? { borderColor: '#B4592F' } : undefined}
        />
        <span className="oil-panel-sub">for</span>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`ordering-tab ${scope === t.key ? 'active' : ''}`}
            style={{ padding: '5px 10px', fontSize: 13 }}
            onClick={() => setScope(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button className="low-stock-refresh" onClick={start}>Plan buys</button>
        {inputError && <span className="oil-panel-sub" style={{ color: '#B4592F' }}>enter a dollar amount first</span>}
        {budget && !settled && <span className="oil-panel-sub">computing {TAB_LABEL[scope]}…</span>}
        {budget && plan && (
          <button className="oil-link-btn" onClick={() => { setBudget(null); setInput(''); }}>clear</button>
        )}
      </div>
      {budget && failed && (
        <div className="ordering-global-note">
          {TAB_LABEL[scope]} failed to load — {String(errorByType[scope])}
        </div>
      )}
      {plan && (
        <>
          <div className="oil-kpi-strip budget-kpis">
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(plan.spent)}</span><span className="oil-kpi-label">planned spend</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{plan.taken.length}</span><span className="oil-kpi-label">buys funded</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(plan.remaining)}</span><span className="oil-kpi-label">left over</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(plan.unmetUsd)}</span><span className="oil-kpi-label">still unfunded need</span></div>
          </div>
          {byVendor.size > 0 && (
            <div className="oil-chip-cell" style={{ padding: '0 16px 10px' }}>
              {[...byVendor.entries()].sort((a, b) => b[1].usd - a[1].usd).map(([key, v]) => (
                <span key={key} className="oil-chip">
                  {trimVendor(v.vendorName)}: {v.lines.length} item{v.lines.length > 1 ? 's' : ''} · ~{fmtMoney(v.usd)}
                  {' '}
                  {v.kind === 'weight'
                    ? <WeightDraftPoButton vendorId={v.vendorId} vendorName={v.vendorName} lines={v.lines} />
                    : <EachDraftPoButton vendorNsId={v.vendorNsId} vendorName={v.vendorName} lines={v.lines} />}
                </span>
              ))}
            </div>
          )}
          <table className="oil-list-table">
            <thead>
              <tr>
                <th>#</th><th>Why</th><th>Item</th><th>Vendor</th>
                <th className="r">Buy</th><th className="r">Unit cost</th><th className="r">Cost</th>
                <th className="r">Running total</th><th>Order by</th>
              </tr>
            </thead>
            <tbody>
              {plan.taken.map((l, i) => {
                running += l.cost;
                return (
                  <tr key={l.key}>
                    <td>{i + 1}</td>
                    <td>
                      <span className={`oil-pill ${l.tier <= 1 ? 'oil-pill-serious' : l.tier === 2 ? 'oil-pill-warning' : 'oil-pill-info'}`}>{PLAN_TIERS[l.tier]}</span>
                      {l.verifyPo && <span className="oil-sku-vendor" title="An open PO already covers some of this — confirm it was actually sent/paid before ordering more"> verify open PO first</span>}
                      {l.partial && <span className="oil-sku-vendor"> partial — {fmtQty(l.maxQty)} {l.unit} suggested</span>}
                    </td>
                    <td>{l.sku}<span className="oil-sku-vendor" style={{ marginLeft: 6 }}>{l.name}</span></td>
                    <td className="oil-cust">{trimVendor(l.vendorName)}</td>
                    <td className="r">{fmtQty(l.qty)} {l.unit}</td>
                    <td className="r">${l.unitCost.toFixed(2)}</td>
                    <td className="r">{fmtMoney(l.cost)}</td>
                    <td className="r">{fmtMoney(running)}</td>
                    <td>{l.orderBy ? fmtDate(l.orderBy) : '—'}</td>
                  </tr>
                );
              })}
              {plan.taken.length === 0 && (
                <tr><td colSpan={9} className="ordering-global-note">{`Nothing fits — either ${TAB_LABEL[scope].toLowerCase()} has no suggested buys right now, or the budget is below the smallest order increment.`}</td></tr>
              )}
            </tbody>
          </table>
          {(plan.skipped.length > 0 || plan.noCost.length > 0) && (
            <div className="ordering-global-note">
              {plan.skipped.length > 0 && <>Didn't fit: {plan.skipped.length} more suggested buy{plan.skipped.length > 1 ? 's' : ''} (~{fmtMoney(plan.skipped.reduce((s, c) => s + c.maxQty * c.unitCost, 0))}) — raise the budget to fund the next tier down.</>}
              {plan.noCost.length > 0 && <> {plan.noCost.length} item{plan.noCost.length > 1 ? 's have' : ' has'} no cost on file ({plan.noCost.slice(0, 5).map((c) => c.sku).join(', ')}{plan.noCost.length > 5 ? ', …' : ''}) — review manually.</>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EACH (vessels / wicks / labels / packaging / other) — the mirror engine +
// seasonal enrichment. Editable buys, per-vendor PO builder, delivery panel.
// ════════════════════════════════════════════════════════════════════════════

// Arm-then-confirm draft-PO button — same interaction as the weight one,
// posting to the materials native-first path. Weight-based rows (isWeight)
// send the flag so the server applies the lb→oz unit rules.
function EachDraftPoButton({ vendorNsId, vendorName, lines, onCreated }) {
  const [state, setState] = useState('idle'); // idle -> armed -> creating -> done | error
  const [result, setResult] = useState(null);
  if (lines.length === 0) return null;
  if (state === 'done' && result) {
    if (!result.tranid && result.nativePoId) {
      return (
        <span className="oil-chip" title={result.ns_error || undefined}>
          Saved natively{result.native_po_number ? ` (${result.native_po_number})` : ''}
          {result.total ? ` · ${fmtMoney(result.total)}` : ''} — NetSuite copy pending, syncs automatically
        </span>
      );
    }
    return (
      <span className="oil-chip">
        Drafted {result.ns_url
          ? <a href={result.ns_url} target="_blank" rel="noreferrer">{result.tranid}</a>
          : result.tranid}{result.total ? ` · ${fmtMoney(result.total)}` : ''} — review &amp; send in NetSuite
      </span>
    );
  }
  const create = async () => {
    setState('creating');
    try {
      const res = await axios.post('/api/materials-ordering/draft-po', {
        vendorNsId: vendorNsId || undefined, vendorName, lines,
      }, { timeout: 60000 });
      setResult(res.data);
      setState('done');
      if (onCreated) onCreated(res.data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || e.message });
      setState('error');
    }
  };
  if (state === 'error') {
    return (
      <span className="oil-chip oil-chip-short" title={result?.error}>
        PO failed — {String(result?.error || '').slice(0, 80)}{' '}
        <button className="oil-link-btn" onClick={() => setState('idle')}>retry</button>
      </span>
    );
  }
  const units = Math.round(lines.reduce((s, l) => s + l.quantity, 0));
  return (
    <button
      className={`oil-draft-po-btn ${state === 'armed' ? 'armed' : ''}`}
      disabled={state === 'creating'}
      onClick={(e) => { e.stopPropagation(); state === 'armed' ? create() : setState('armed'); }}
      onBlur={() => state === 'armed' && setState('idle')}
      title={`Creates a draft PO (native first; NetSuite pending approval, not emailed): ${lines.length} item${lines.length > 1 ? 's' : ''}, ${units.toLocaleString()} units`}
    >
      {state === 'creating' ? 'Creating…' : state === 'armed' ? 'Confirm draft PO?' : 'Draft PO'}
    </button>
  );
}

// Per-vendor rollup of the (editable) buy quantities.
function VendorRollup({ rows, buys }) {
  const byVendor = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const qty = buys[r.sku] ?? r.suggestedBuy;
      if (!(qty > 0)) continue;
      const key = r.vendorName || 'No vendor';
      const v = map.get(key) || { vendorNsId: r.vendorNsId, usd: 0, units: 0, lines: [] };
      if (!v.vendorNsId && r.vendorNsId) v.vendorNsId = r.vendorNsId;
      v.usd += qty * (r.lastCost || 0);
      v.units += qty;
      v.lines.push({ sku: r.sku, quantity: qty, unitCost: r.lastCost || 0, isWeight: r.isWeight === true });
      map.set(key, v);
    }
    return map;
  }, [rows, buys]);
  if (byVendor.size === 0) return null;
  return (
    <div className="oil-panel">
      <div className="oil-panel-head">
        <h3>PO builder</h3>
        <span className="oil-panel-sub">
          buy quantities rolled up per vendor — edits in the table feed these buttons; drafts save natively first, then NetSuite (pending approval, not emailed)
        </span>
      </div>
      <div className="oil-chip-cell" style={{ padding: '0 16px 14px' }}>
        {[...byVendor.entries()].sort((a, b) => b[1].usd - a[1].usd).map(([name, v]) => (
          <span key={name} className="oil-chip">
            {name}: {v.lines.length} item{v.lines.length > 1 ? 's' : ''} · {fmtQty(v.units)} units · ~{fmtMoney(v.usd)}
            {' '}<EachDraftPoButton vendorNsId={v.vendorNsId} vendorName={name} lines={v.lines} />
          </span>
        ))}
      </div>
    </div>
  );
}

// Open POs with expected delivery — firmed (line/header date) vs estimated
// (order date + default lead time), overdue flagged, soonest first.
function OpenPoPanel({ pos, className }) {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(null);
  if (!pos || pos.length === 0) {
    return (
      <div className="oil-panel">
        <div className="oil-panel-head">
          <h3>Open POs — {className}</h3>
          <span className="oil-panel-sub">none open in the last year</span>
        </div>
      </div>
    );
  }
  const overdueCount = pos.filter((p) => p.overdue).length;
  return (
    <div className="oil-panel">
      <button className="oil-panel-toggle" onClick={() => setOpen((s) => !s)}>
        <span className="oil-panel-caret">{open ? '▾' : '▸'}</span>
        Open POs — {pos.length} covering {className}
        {overdueCount > 0 && <span className="oil-warn-tag">{overdueCount} past expected delivery</span>}
      </button>
      {open && (
        <table className="oil-list-table">
          <thead>
            <tr>
              <th>PO</th><th>Vendor</th><th>Ordered</th>
              <th>Expected delivery</th><th className="r">Items</th><th>Lines</th>
            </tr>
          </thead>
          <tbody>
            {pos.map((p) => {
              const isOpen = expanded === String(p.poId);
              return (
                <React.Fragment key={p.poId}>
                  <tr className="oil-list-row" onClick={() => setExpanded(isOpen ? null : String(p.poId))}>
                    <td>
                      {p.poNumber}
                      {p.native && p.nsStatus === 'pending' && (
                        <span className="oil-pill oil-pill-info" style={{ marginLeft: 6 }}>native — NS pending</span>
                      )}
                    </td>
                    <td className="oil-cust">{p.vendorName}</td>
                    <td>{fmtDate(p.orderDate)}{p.stale ? <span className="oil-num-short"> · {p.ageDays}d old</span> : ''}</td>
                    <td className={p.overdue ? 'oil-num-short' : ''}>
                      {fmtDate(p.expectedDelivery)}
                      <span className="oil-sku-vendor" style={{ marginLeft: 6 }}>
                        {p.deliverySource === 'firmed' ? 'firmed' : 'est. from lead time'}
                      </span>
                      {p.overdue ? ' · overdue' : ''}
                    </td>
                    <td className="r">{p.lines.length}</td>
                    <td className="oil-chip-cell">
                      {p.lines.slice(0, 3).map((l) => (
                        <span key={l.sku} className="oil-chip">{l.sku} · {fmtQty(l.remaining)}{l.unit === 'lb' ? ' lb' : ''}</span>
                      ))}
                      {p.lines.length > 3 && <span className="oil-chip">+{p.lines.length - 3}</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="oil-detail-row"><td colSpan={6}>
                      <table className="oil-mini-table oil-mini-pad">
                        <thead><tr><th>Item</th><th className="r">Remaining</th><th>Expected</th><th>Basis</th></tr></thead>
                        <tbody>
                          {p.lines.map((l) => (
                            <tr key={l.sku}>
                              <td>{l.sku}</td>
                              <td className="r">{fmtQty(l.remaining)}{l.unit === 'lb' ? ' lb' : ''}</td>
                              <td>{fmtDate(l.expectedDelivery)}</td>
                              <td className="oil-mini-sku">{l.deliverySource === 'firmed' ? 'firmed' : 'est. from lead time'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EachTab({ typeKey, data, loading, error, onRefresh }) {
  const [buys, setBuys] = useState({}); // sku -> edited buy qty
  const [search, setSearch] = useState('');
  const [onlyAction, setOnlyAction] = useState(true);
  const [sortKey, setSortKey] = useState('daysOfCover');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(['sku', 'vendorName'].includes(key) ? 'asc' : 'desc'); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    let out = (data.rows || []).filter((r) =>
      (!onlyAction || r.suggestedBuy > 0 || r.flags.some((f) => f !== 'OK')) &&
      (!q || r.sku.toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q) || (r.vendorName || '').toLowerCase().includes(q))
    );
    out = [...out].sort((a, b) => {
      const av = a[sortKey] ?? (sortKey === 'daysOfCover' ? Infinity : -Infinity);
      const bv = b[sortKey] ?? (sortKey === 'daysOfCover' ? Infinity : -Infinity);
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return out;
  }, [data, search, onlyAction, sortKey, sortDir]);

  const kpis = useMemo(() => {
    if (!data) return null;
    const all = data.rows || [];
    const needOrder = all.filter((r) => r.suggestedBuy > 0).length;
    const estCost = all.reduce((s, r) => s + ((buys[r.sku] ?? r.suggestedBuy) * (r.lastCost || 0)), 0);
    const overdue = (data.open_pos || []).filter((p) => p.overdue).length;
    return { needOrder, estCost, openPos: (data.open_pos || []).length, overdue };
  }, [data, buys]);

  if (loading && !data) {
    return (
      <div>
        <LoadingSpinner />
        <p className="oil-loading-note">Computing mirror demand plus last-year seasonal consumption — up to a minute on first load for large types.</p>
      </div>
    );
  }
  if (error) return <div className="message-box error">{error}</div>;
  if (!data) return null;

  return (
    <>
      <div className="oil-top">
        {kpis && (
          <div className="oil-kpi-strip" style={{ marginLeft: 0 }}>
            <div className="oil-kpi"><span className="oil-kpi-value">{kpis.needOrder}</span><span className="oil-kpi-label">items to order</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{fmtMoney(kpis.estCost)}</span><span className="oil-kpi-label">est. buy cost</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{kpis.openPos}</span><span className="oil-kpi-label">open POs</span></div>
            <div className="oil-kpi"><span className="oil-kpi-value">{kpis.overdue}</span><span className="oil-kpi-label">POs past due-in</span></div>
            {data.trend && <div className="oil-kpi" title={`Base-business consumption of ${typeKey}, trailing 6 complete months vs same months last year (raw ×${data.trend.raw}, clamped 0.5–1.5). Scales the seasonal column.`}><span className="oil-kpi-value">×{data.trend.factor}</span><span className="oil-kpi-label">6-mo YoY trend</span></div>}
          </div>
        )}
        <div className="oil-header-controls" style={{ marginLeft: 'auto' }}>
          <button className="low-stock-refresh" onClick={onRefresh}>Refresh</button>
        </div>
      </div>

      <details className="oil-how">
        <summary>How these numbers work</summary>
        <p>
          Demand is the LARGER of two lenses. (1) Committed: every open work order (NetSuite mirror + native,
          deduplicated) exploded one BOM level into component requirements — a re-skinned collection automatically
          demands its NEW components because the math follows the actual WOs. (2) Seasonal: consumption in the same
          calendar window last year from NetSuite build history (base business only — program builds for named
          customers like B&N are excluded, since repeating programs arrive via their own WOs), scaled by this
          type's 6-month year-over-year trend. Velocity is trailing-90-day consumption from the build feed and is
          context only; during a SKU transition it overstates discontinued components (flagged "demand may be
          discontinued" when most burn came from inactive or idle finished goods) and understates brand-new ones
          (flagged "new — no history"). Suggested buy = demand plus {data?.safety_days ?? 14} days of velocity
          cover, minus on-hand and fresh on-order, rounded up to the item's reorder multiple. Order-by = projected
          run-out minus vendor lead time (item lead time, default {data?.default_lead_time_days ?? 14} days).
          Expected PO deliveries use the firmed line/header date when one exists, otherwise order date + lead time.
          {data?.enriched === false && ' NOTE: NetSuite was unreachable — seasonal columns are blank and demand is work-order-committed only until the next refresh.'}
        </p>
      </details>

      <div className="oil-controls">
        <label className="oil-check">
          <input type="checkbox" checked={onlyAction} onChange={(e) => setOnlyAction(e.target.checked)} />
          Needs attention
        </label>
        <input className="low-stock-search" type="text" placeholder="Search item or vendor..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="oil-count">{rows.length} items</span>
      </div>

      <VendorRollup rows={data?.rows || []} buys={buys} />
      <div className="low-stock-table-wrap">
        <table className="low-stock-table oil-table">
          <thead>
            <tr>
              <Th label="Item" col="sku" {...{ sortKey, sortDir, handleSort }} />
              <Th label="On hand" col="onHand" {...{ sortKey, sortDir, handleSort }} align="right" title="Native ledger on-hand, all locations" />
              <Th label="On order" col="onOrder" {...{ sortKey, sortDir, handleSort }} align="right" title="Open POs (fresh, last year), ordered − received; next arrival shown underneath" />
              <Th label="WO committed" col="woCommitted" {...{ sortKey, sortDir, handleSort }} align="right" title="Open work orders (NS + native, deduped) × BOM qty per unit" />
              <Th label="Seasonal" col="seasonal_trend_units" {...{ sortKey, sortDir, handleSort }} align="right" title="Same window last year (base business, program builds excluded) × the type's 6-mo YoY trend" />
              <Th label="Use / day" col="dailyRate" {...{ sortKey, sortDir, handleSort }} align="right" title="Trailing-90d build consumption (context — feed is young)" />
              <Th label="Cover" col="daysOfCover" {...{ sortKey, sortDir, handleSort }} align="right" title="(on hand + on order) ÷ daily use" />
              <Th label="Short" col="shortfall" {...{ sortKey, sortDir, handleSort }} align="right" title="max(WO demand, seasonal) + safety − on hand − on order" />
              <th className="low-stock-th low-stock-th-right" title="Editable — feeds the PO builder above">Buy</th>
              <Th label="Vendor" col="vendorName" {...{ sortKey, sortDir, handleSort }} />
              <Th label="Unit cost" col="lastCost" {...{ sortKey, sortDir, handleSort }} align="right" title="item_vendors last cost, else last purchase price / average cost ($/unit; $/lb for weight-based rows)" />
              <Th label="Est cost" col="estCost" {...{ sortKey, sortDir, handleSort }} align="right" />
              <Th label="Order by" col="orderBy" {...{ sortKey, sortDir, handleSort }} title="Run-out at the fastest of velocity / WO burn / seasonal pace, minus lead time" />
              <th className="low-stock-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const buy = buys[r.sku] ?? r.suggestedBuy;
              const isLb = r.unit === 'lb';
              const coverCls = r.daysOfCover == null ? '' : r.daysOfCover < r.leadTimeDays ? 'oil-cover-critical' : r.daysOfCover < r.leadTimeDays + 14 ? 'oil-cover-low' : '';
              return (
                <tr key={r.itemId} className="oil-row">
                  <td className="oil-cell-sku">
                    <span className="oil-sku-block">
                      {r.sku}
                      {r.name !== r.sku && <span className="oil-sku-vendor">{r.name}</span>}
                    </span>
                  </td>
                  <td className={`low-stock-num ${r.onHand <= 0 ? 'oil-num-zero' : ''}`}>{fmtQty(r.onHand)}{isLb ? ' lb' : ''}</td>
                  <td className="low-stock-num oil-num-dim">
                    {r.onOrder > 0 ? `${fmtQty(r.onOrder)}${isLb ? ' lb' : ''}` : '—'}
                    {r.nextArrival && (
                      <span className="oil-sku-vendor" title={r.nextArrivalSource === 'firmed' ? 'Firmed delivery date' : 'Estimated from order date + lead time'}>
                        {fmtDate(r.nextArrival)} {r.nextArrivalSource === 'firmed' ? '(firmed)' : '(est.)'}
                      </span>
                    )}
                  </td>
                  <td className="low-stock-num">{r.woCommitted > 0 ? `${fmtQty(r.woCommitted)}${isLb ? ' lb' : ''}` : '—'}</td>
                  <td className="low-stock-num">{r.seasonal_trend_units > 0 ? `${fmtQty(r.seasonal_trend_units)}${isLb ? ' lb' : ''}` : '—'}</td>
                  <td className="low-stock-num">
                    {r.dailyRate > 0 ? r.dailyRate.toFixed(1) : '—'}
                    {r.staleShare != null && r.staleShare > 0.5 && (
                      <span className="oil-sku-vendor" title={`${Math.round(r.staleShare * 100)}% of trailing consumption came from finished goods that are inactive or have no open work orders — likely a SKU transition`}>
                        {Math.round(r.staleShare * 100)}% stale
                      </span>
                    )}
                  </td>
                  <td className={`low-stock-num ${coverCls}`}>{r.daysOfCover != null ? `${Math.round(r.daysOfCover)}d` : '—'}</td>
                  <td className={`low-stock-num ${r.shortfall > 0 ? 'oil-num-short' : ''}`}>{r.shortfall > 0 ? fmtQty(r.shortfall) : '—'}</td>
                  <td className="low-stock-num oil-num-rec">
                    <input
                      type="number"
                      min="0"
                      step={r.reorderMultiple || 1}
                      value={buy}
                      onChange={(e) => setBuys((b) => ({ ...b, [r.sku]: Math.max(0, Math.round(Number(e.target.value) || 0)) }))}
                      style={{ width: 76, textAlign: 'right', border: '1px solid #E5E1D8', borderRadius: 6, padding: '2px 6px', background: buy !== r.suggestedBuy ? '#FDF7EE' : 'white' }}
                      title={`Suggested ${r.suggestedBuy.toLocaleString()}${isLb ? ' lb' : ''} (multiple of ${r.reorderMultiple})`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="oil-cust">{r.vendorName}</td>
                  <td className="low-stock-num oil-num-dim">{r.lastCost > 0 ? `$${r.lastCost.toFixed(2)}${isLb ? '/lb' : ''}` : '—'}</td>
                  <td className="low-stock-num">{buy > 0 && r.lastCost > 0 ? fmtMoney(buy * r.lastCost) : '—'}</td>
                  <td>{fmtDate(r.orderBy)}</td>
                  <td className="oil-pills">
                    {r.flags.filter((f) => f !== 'OK').slice(0, 2).map((f) => (
                      <span key={f} className={`oil-pill ${FLAG_META[f]?.cls || 'oil-pill-muted'}`}>{FLAG_META[f]?.label || f}</span>
                    ))}
                    {r.flags.includes('OK') && <span className="oil-pill oil-pill-ok">OK</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div className="no-data">No items match the current filters.</div>}
      </div>
      <OpenPoPanel pos={data?.open_pos || []} className={typeKey} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Page shell — tabs, lazy per-type fetch, page-global watch + trend.
// ════════════════════════════════════════════════════════════════════════════

function OrderingDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TAB_KIND[tabParam] ? tabParam : 'oils';

  const [dataByType, setDataByType] = useState({});
  const [loadingByType, setLoadingByType] = useState({});
  const [errorByType, setErrorByType] = useState({});
  const [horizon, setHorizon] = useState(60);
  const horizonRef = useRef(60);
  const inFlight = useRef(new Set());

  const fetchType = useCallback(async (typeKey, refresh = false) => {
    if (inFlight.current.has(typeKey)) return;
    inFlight.current.add(typeKey);
    setLoadingByType((s) => ({ ...s, [typeKey]: true }));
    setErrorByType((s) => ({ ...s, [typeKey]: null }));
    try {
      const params = { type: typeKey };
      if (TAB_KIND[typeKey] === 'weight') params.horizon = horizonRef.current;
      if (refresh) params.refresh = 1;
      const res = await axios.get('/api/ordering/summary', { params, timeout: 300000 });
      setDataByType((s) => ({ ...s, [typeKey]: res.data }));
    } catch (err) {
      setErrorByType((s) => ({ ...s, [typeKey]: err.response?.data?.error || 'Failed to load ordering data' }));
    } finally {
      inFlight.current.delete(typeKey);
      setLoadingByType((s) => ({ ...s, [typeKey]: false }));
    }
  }, []);

  // Lazy per-tab compute — a tab's payload loads when the tab is opened.
  // The Critical tab loads its own data (CriticalMaterialsTab).
  useEffect(() => {
    if (tab === 'critical') return;
    if (!dataByType[tab] && !loadingByType[tab] && !errorByType[tab]) fetchType(tab);
  }, [tab, dataByType, loadingByType, errorByType, fetchType]);

  const changeHorizon = (h) => {
    horizonRef.current = h;
    setHorizon(h);
    // Weight summaries are horizon-scoped — drop them so the effect refetches.
    setDataByType((s) => {
      const next = { ...s };
      delete next.oils;
      delete next.wax;
      return next;
    });
  };

  const setTab = (key) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  const data = dataByType[tab];
  const oilsData = dataByType.oils;
  const asOf = data?.as_of ? new Date(data.as_of).toLocaleString() : '';
  const cacheAge = data?.cache_age_s ? ` · cached ${Math.round(data.cache_age_s / 60)}m ago` : '';

  return (
    <div className="ops-page oil-page">
      <div className="oil-top">
        <button className="back-button" onClick={() => navigate('/ops')}>← Back</button>
        <div className="oil-title">
          <h1>Ordering</h1>
          <span className="oil-asof">{asOf}{cacheAge}</span>
        </div>
      </div>

      {/* "$X to spend on <type>" budget allocator — plans within one type. */}
      {tab !== 'critical' && (
        <BudgetPlanner
          activeTab={tab}
          dataByType={dataByType}
          errorByType={errorByType}
          loadingByType={loadingByType}
          fetchType={fetchType}
        />
      )}

      <div className="ordering-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`ordering-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Named-customer production watch — oils and vessels each have their
          own (oil engine / mirror+SuiteQL WO pass respectively). */}
      {tab === 'oils' && oilsData && (
        <CommittedOrders orders={oilsData.committed_orders || []} />
      )}
      {tab === 'vessels' && dataByType.vessels && (
        <EachCommittedOrders orders={dataByType.vessels.committed_orders || []} noun="vessels" />
      )}

      {tab === 'critical' ? (
        <CriticalMaterialsTab />
      ) : TAB_KIND[tab] === 'weight' ? (
        <WeightTab
          key={tab}
          typeKey={tab}
          data={data}
          loading={!!loadingByType[tab]}
          error={errorByType[tab]}
          horizon={horizon}
          onHorizonChange={changeHorizon}
          onRefresh={() => fetchType(tab, true)}
        />
      ) : (
        <EachTab
          key={tab}
          typeKey={tab}
          data={data}
          loading={!!loadingByType[tab]}
          error={errorByType[tab]}
          onRefresh={() => fetchType(tab, true)}
        />
      )}
    </div>
  );
}

export default OrderingDashboard;
